import React, { useState, useEffect } from 'react'
import styles from './MovieModal.module.scss'
import { getMovieDetails, getMovieReviews, addMovieReview, getSimilarMovies } from 'service/MovieService'
import ImageWithFallback from 'components/ImageWithFallback/ImageWithFallback'
import { MovieIcon, StarIcon, FilmIcon, PlayIcon, HeartIcon } from 'components/CustomIcons'

const MovieModal = ({ 
  movie, 
  onClose, 
  onToggleFavorite, 
  onRate, 
  isFavorite,
  onWatch
}) => {
  const [movieDetails, setMovieDetails] = useState(null)
  const [activeTab, setActiveTab] = useState('info')
  const [userRating, setUserRating] = useState(movie.user_rating || 0)
  const [reviews, setReviews] = useState([])
  const [newReview, setNewReview] = useState('')
  const [similarMovies, setSimilarMovies] = useState([])

  useEffect(() => {
    loadMovieDetails()
    loadReviews()
    loadSimilarMovies()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movie.slug])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const loadMovieDetails = async () => {
    try {
      const details = await getMovieDetails(movie.slug)
      setMovieDetails(details)
    } catch (error) {
      console.error('Error loading movie details:', error)
    }
  }

  const loadReviews = async () => {
    try {
      const reviewsData = await getMovieReviews(movie.slug)
      setReviews(reviewsData.results || [])
    } catch (error) {
      console.error('Error loading reviews:', error)
    }
  }

  const loadSimilarMovies = async () => {
    try {
      const similar = await getSimilarMovies(movie.slug)
      setSimilarMovies(similar.results?.slice(0, 6) || [])
    } catch (error) {
      console.error('Error loading similar movies:', error)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleRatingClick = async (rating) => {
    setUserRating(rating)
    await onRate(rating)
    // Перезагружаем детали фильма для обновления рейтинга
    setTimeout(async () => {
      await loadMovieDetails()
      await loadReviews()
    }, 800)
  }

  const submitReview = async () => {
    if (!newReview.trim()) return
    
    try {
      await addMovieReview(movie.slug, newReview, userRating)
      setNewReview('')
      // Перезагружаем детали и отзывы для обновления рейтинга
      setTimeout(async () => {
        await loadMovieDetails()
        await loadReviews()
      }, 800)
    } catch (error) {
      console.error('Error submitting review:', error)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Недавно'
    const parsedDate = new Date(date)
    if (isNaN(parsedDate.getTime())) return 'Недавно'
    return parsedDate.toLocaleDateString('ru-RU')
  }

  const details = movieDetails || movie

  return (
    <div className={styles.modalOverlay} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <button className={styles.closeButton} onClick={onClose}>
          ✕
        </button>

        <div className={styles.modalContent}>
          <div className={styles.movieHeader}>
            <div className={styles.posterSection}>
              <div className={styles.posterWrapper}>
                <ImageWithFallback
                  src={details.poster 
                    ? (details.poster.startsWith('http') ? details.poster : `https://security.ncore.kg${details.poster}`)
                    : null
                  }
                  alt={details.name || details.title}
                  className={styles.modalPoster}
                  fallbackIcon="🎬"
                  fallbackText="Нет постера"
                />
              </div>
              
              <div className={styles.actionButtons}>
                <button 
                  className={styles.watchBtn}
                  onClick={onWatch}
                >
                  <PlayIcon size={18} style={{ marginRight: '6px' }} /> Смотреть
                </button>
                
                <button 
                  className={`${styles.favoriteBtn} ${isFavorite ? styles.active : ''}`}
                  onClick={onToggleFavorite}
                >
                  <HeartIcon size={18} style={{ color: isFavorite ? '#ff4444' : '#999', marginRight: '6px' }} /> {isFavorite ? 'В избранном' : 'В избранное'}
                </button>

              </div>
            </div>

            <div className={styles.movieInfo}>
              <h1 className={styles.modalTitle}>{details.name || details.title}</h1>

              <div className={styles.movieMeta}>
                <span className={styles.year}>{formatDate(details.release_date)}</span>
                {details.type && (
                  <span className={styles.type}>
                    <MovieIcon size={16} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                    {details.type === 'movie' ? 'Фильм' : 'Сериал'}
                  </span>
                )}
                <span className={styles.rating}>
                  <StarIcon size={16} filled={true} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                  {details.rating_avg ? parseFloat(details.rating_avg).toFixed(1) : 'N/A'} ({details.reviews_count || 0} отзывов)
                </span>
              </div>

              <div className={styles.genres}>
                {details.categories_names?.map((categoryName, index) => (
                  <span key={index} className={styles.genre}>
                    {categoryName}
                  </span>
                ))}
              </div>

              {details.description && (
                <p className={styles.overview}>{details.description}</p>
              )}

              <div className={styles.userRating}>
                <h4>Ваша оценка:</h4>
                <div className={styles.stars}>
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`${styles.star} ${star <= userRating ? styles.filled : ''}`}
                      onClick={() => handleRatingClick(star)}
                    >
                      <StarIcon size={20} filled={star <= userRating} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.tabsContainer}>
            <div className={styles.tabs}>
              <button 
                className={`${styles.tab} ${activeTab === 'info' ? styles.active : ''}`}
                onClick={() => setActiveTab('info')}
              >
                Информация
              </button>
              <button 
                className={`${styles.tab} ${activeTab === 'reviews' ? styles.active : ''}`}
                onClick={() => setActiveTab('reviews')}
              >
                Отзывы ({reviews.length})
              </button>
              <button 
                className={`${styles.tab} ${activeTab === 'similar' ? styles.active : ''}`}
                onClick={() => setActiveTab('similar')}
              >
                Похожие
              </button>
            </div>

            <div className={styles.tabContent}>
              {activeTab === 'info' && (
                <div className={styles.infoTab}>
                  <div className={styles.infoItem}>
                    <strong>Тип:</strong> {details.type === 'movie' ? 'Фильм' : 'Сериал'}
                  </div>
                  
                  {details.release_date && (
                    <div className={styles.infoItem}>
                      <strong>Дата выхода:</strong> {formatDate(details.release_date)}
                    </div>
                  )}
                  
                  {details.description && (
                    <div className={styles.infoItem}>
                      <strong>Описание:</strong>
                      <p>{details.description}</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'reviews' && (
                <div className={styles.reviewsTab}>
                  <div className={styles.writeReview}>
                    <h4>Написать отзыв:</h4>
                    <textarea
                      value={newReview}
                      onChange={(e) => setNewReview(e.target.value)}
                      placeholder="Поделитесь своим мнением о фильме..."
                      className={styles.reviewTextarea}
                    />
                    <button 
                      onClick={submitReview}
                      className={styles.submitReview}
                      disabled={!newReview.trim()}
                    >
                      Опубликовать отзыв
                    </button>
                  </div>

                  <div className={styles.reviewsList}>
                    {reviews.map(review => (
                      <div key={review.id} className={styles.review}>
                        <div className={styles.reviewHeader}>
                          <strong>{review.user_username || review.author}</strong>
                          <span className={styles.reviewDate}>
                            {formatDate(review.created_at)}
                          </span>
                        </div>
                        <div className={styles.reviewRating}>
                          <StarIcon size={14} filled={true} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                          {review.rating}/5
                        </div>
                        <p className={styles.reviewContent}>{review.body || review.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'similar' && (
                <div className={styles.similarTab}>
                  <div className={styles.similarGrid}>
                    {similarMovies.map(similarMovie => (
                      <div key={similarMovie.id} className={styles.similarMovie}>
                        <ImageWithFallback
                          src={similarMovie.poster 
                            ? (similarMovie.poster.startsWith('http') ? similarMovie.poster : `https://security.ncore.kg${similarMovie.poster}`)
                            : null
                          }
                          alt={similarMovie.name || similarMovie.title}
                          fallbackIcon="🎬"
                          fallbackText=""
                        />
                        <h5>{similarMovie.name || similarMovie.title}</h5>
                        <p>
                          <StarIcon size={14} filled={true} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} />
                          {similarMovie.rating_avg ? parseFloat(similarMovie.rating_avg).toFixed(1) : 'N/A'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MovieModal